import { CellModel, CellSource, ColumnSource, RowSource } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { ModuleCode, MODULE_CODES } from "@reporting/rp-types/accounting";
import { DimensionDefinition, PropertiesDefinition } from "@reporting/rp-types/definition";
import { MODULES_PREFIX, DIM_ORDER_TYPES, DIM_VIEW_TYPES } from "../types";
import { EntityAccessType } from "@reporting/rp-types/users";

export class DimensionGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	constructor (
		private moduleCode: ModuleCode
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 3,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'attributes',
				title: msg('Attributes'),
				icon: 'brackets-curly',
				color: ColorTypes.Gray
			}, {
				action: 'access',
				title: msg('Access'),
				icon: 'shield-check',
				color: ColorTypes.Gray
			}],
			filter: false,
			sorting: false,
		}, {
			data: '_dim',
			header: msg('Dimension'),
			t: 't',
			e: false,
			w: 100
		}, {
			data: '_table',
			header: msg('Table'),
			t: 't',
			e: false,
			w: 130
		}, {
			data: '_propsCount',
			header: msg('Attributes'),
			t: 'n',
			e: false,
			w: 80
		}, {
			data: 'enabled',
			header: msg('Show'),
			t: 'b',
			e: true,
			w: 70,

		}, {
			data: 'ord_num',
			header: msg('Order'),
			t: 'n',
			e: true,
			w: 60
		}, {
			data: 'description',
			header: msg('Name'),
			t: 't',
			e: true,
			w: 200
		}, {
			data: 'item_ord',
			header: msg('Sorting'),
			t: 's',
			e: true,
			w: 80,
			content: DIM_ORDER_TYPES.map(item => ({ k: item.k, v: msg(item.v) }))
		}, {
			data: 'item_view',
			header: msg('View'),
			t: 's',
			e: true,
			w: 100,
			content: DIM_VIEW_TYPES.map(item => ({ k: item.k, v: msg(item.v) }))
		}, {
			data: 'default_value',
			header: msg('Default value'),
			t: 't',
			e: true,
			w: 130
		}];
		if (this.moduleCode == MODULE_CODES.FINANCE) {
			this._columns.push({
				data: 'period_close',
				header: msg('Period closing'),
				t: 'b',
				e: true,
				w: 130,}
			);
		} else if (this.moduleCode == MODULE_CODES.ITEMS) {
			this._columns.push({
				data: 'balance',
				header: msg('Balance calculation'),
				t: 'b',
				e: true,
				w: 150,}
			);
		}
		// hidden columns
		this._columns.push({
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true
		}, {
			data: 'dim_num',
			t: 'n',
			e: false,
			hidden: true
		}, {
			data: 'module',
			t: 't',
			e: false,
			hidden: true
		})
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const dims = await rp.ms('definition').get<DimensionDefinition[]>(`dimensions/${this.moduleCode}`)
		const rows = dims.map(data => {
			// convert properties to array if needed
			const properties = data.properties ? (Array.isArray(data.properties) ? data.properties : Object.values(data.properties)) : [];
			return {
				data: {
					_dim: `${msg('Dimension')} ${data.dim_num}`,
					_table: `D_${MODULES_PREFIX[this.moduleCode]}DIM_${data.dim_num}`,
					_propsCount: data.idz ? properties.length : null,
					...data,
					item_ord: this.createItemOrderCell(properties, data.item_ord),
				}
			}
		});
		return rows;
	}

	createItemOrderCell(properties: PropertiesDefinition[], itemOrd: string): string|Partial<CellSource> {
		if (!properties || properties.length === 0) return itemOrd;
		const orderList = DIM_ORDER_TYPES.map(item => ({ k: item.k, v: msg(item.v) }));
		for (const prop of properties) {
			orderList.push({ k: `A:${prop.key}`, v: `${msg('Attribute')}: ${prop.name}` });
		}
		return { v: itemOrd, content: orderList };
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			refreshAfterSave: true,
			toolbar: {
				new: {enabled: false},
			},
			pageSize: null,
			rowMenu: {
				deleteRows: true,
			},
		});
	}

	onAction(action: string, cell: CellModel): void {
		const data = cell?.row?.data as DimensionDefinition;
		switch (action) {
			case 'attributes':
				if (!data.idz) {
					rp.host.bucket.showAlert({content: msg('Save the row before editing.'), type: 'warning'});
					return;
				}
				rp.host.open(`definition-dimAttributes-${this.moduleCode}-${data.dim_num}`, {title: `${data.description} ${msg('attributes')}`});
				break;
			case 'access':
				const entity = this.moduleCode == MODULE_CODES.FINANCE ? EntityAccessType.FinanceDim
					: this.moduleCode == MODULE_CODES.ITEMS ? EntityAccessType.ItemsDim
					: this.moduleCode == MODULE_CODES.AGING ? EntityAccessType.AgingDim : null;
				if (!entity) return;
				rp.host.open(`users-entityUsers-${entity}-${data.dim_num}`, { title: `${msg('Access')}: ${data.description}` });
				break;
		}
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index}, row.data));
		await rp.ms('definition').post('dimensions', data);
	}

}
